import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Typography,
    Box,
    CircularProgress,
    TextField,
    Snackbar,
    Alert,
    AppBar,
    Toolbar,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tooltip,
    Dialog,
    Slide,
    Paper,
    InputAdornment,
    Fade
} from '@mui/material';
import {
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    Group as GroupIcon,
    Save as SaveIcon,
    ExpandMore as ExpandMoreIcon,
    Refresh as RefreshIcon,
    Computer as ComputerIcon,
    Public as PublicIcon,
    Close as CloseIcon,
    Shield as ShieldIcon,
    Block as BlockIcon,
    CheckCircleOutline as CheckCircleIcon,
    Language as LanguageIcon,
    Dns as DnsIcon,
    School as SchoolIcon,
    Security as SecurityIcon,
    FiberManualRecord as DotIcon
} from '@mui/icons-material';

// Imports Logica e Sottocomponenti
import { useProxyLogic } from './useProxyLogic';
import WhitelistTable from './subcomponents/WhitelistTable';
import BlacklistTable from './subcomponents/BlacklistTable';
import GlobalListsTable from './subcomponents/GlobalListsTable';
import GroupSelectionDialog from './subcomponents/GroupSelectionDialog';
import CreateGroupDialog from './subcomponents/CreateGroupDialog';
import GroupDetailsDialog from './subcomponents/GroupDetailsDialog';
import EditGroupDialog from './subcomponents/EditGroupDialog';

// Brand
const BRAND = '#a71e3b';
const BRAND_DARK = '#8a1830';
const BRAND_LIGHT = 'rgba(167, 30, 59, 0.06)';
const BRAND_BORDER = 'rgba(167, 30, 59, 0.15)';

const SlideTransition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function SectionHeader({ icon, title, subtitle, accent = BRAND }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, pb: 1.5, borderBottom: `1px solid #e0e0e0` }}>
            <Box sx={{
                width: 36, height: 36, borderRadius: 1.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: `${accent}10`,
            }}>
                {React.cloneElement(icon, { sx: { color: accent, fontSize: 18 } })}
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', lineHeight: 1.2 }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
                )}
            </Box>
        </Box>
    );
}

export default function LabConsolleManageProxyPage({ labProps, apiAuthResponse, setIsShowManageProxyLab }) {
    const logic = useProxyLogic(labProps, apiAuthResponse);

    const [showGlobalLists, setShowGlobalLists] = useState(false);
    const [newUrl, setNewUrl] = useState('');
    const [newBlacklistUrl, setNewBlacklistUrl] = useState('');
    const [showGroupDialog, setShowGroupDialog] = useState(false);
    const [showBlacklistGroupDialog, setShowBlacklistGroupDialog] = useState(false);
    const [showGlobalGroupDialog, setShowGlobalGroupDialog] = useState(false);
    const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
    const [groupDetailsDialog, setGroupDetailsDialog] = useState(null);
    const [editGroupDialog, setEditGroupDialog] = useState(null);

    const handleAddUrlClick = () => {
        logic.handleAddUrl(newUrl, () => setNewUrl(''));
    };

    const handleAddBlacklistUrlClick = () => {
        logic.handleAddBlacklistUrl(newBlacklistUrl, () => setNewBlacklistUrl(''));
    };

    const handleViewDetails = async (groupId) => {
        const groupInWhitelist = logic.whitelistUrls.find(item => item.id === groupId && item.type === 'group');

        if (groupInWhitelist) {
            const realGroupId = groupInWhitelist.groupId;
            let groupDetails = logic.availableGroups.find(g => g.idProxyGroup === realGroupId);
            if (!groupDetails) {
                groupDetails = logic.selectedGroups.find(g => g.idProxyGroup === realGroupId);
            }
            if (groupDetails) {
                setGroupDetailsDialog(groupDetails);
                return;
            }

            try {
                const url = process.env.REACT_APP_URL_GET_WHITELIST_GROUP_DETAILS ? `${process.env.REACT_APP_URL_GET_WHITELIST_GROUP_DETAILS}?groupId=${realGroupId}` : null;
                if (url) {
                    const res = await fetch(url, { credentials: 'include' });
                    if (res.ok) {
                        const data = await res.json();
                        setGroupDetailsDialog(data.group);
                        return;
                    }
                }
            } catch (e) {
                console.error("Errore nel fetch dei dettagli del gruppo:", e);
            }

            setGroupDetailsDialog({
                idProxyGroup: realGroupId,
                name: groupInWhitelist.groupName,
                urls: Array.isArray(groupInWhitelist.url) ? groupInWhitelist.url : [groupInWhitelist.url],
                description: ''
            });
        } else {
            const local = logic.availableGroups.find(g => g.idProxyGroup === groupId);
            if (local) {
                setGroupDetailsDialog(local);
            }
        }
    };

    const handleEditGroup = (group) => {
        setEditGroupDialog(group);
        setShowGroupDialog(false);
    };

    const isGroupInWhitelist = (groupId) => {
        return logic.selectedGroups.some(g => g.idProxyGroup === groupId);
    };

    // Render Guard
    if (!labProps) {
        return (
            <AppBar position="sticky" sx={{ bgcolor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
                <Toolbar>
                    <IconButton edge="start" onClick={() => setIsShowManageProxyLab(false)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <SecurityIcon sx={{ color: BRAND, mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: BRAND }}>
                        Gestione Proxy
                    </Typography>
                </Toolbar>
            </AppBar>
        );
    }

    return (
        <React.Fragment>
            {/* === HEADER === */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    bgcolor: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                    borderBottom: `3px solid ${BRAND}`,
                }}
            >
                <Toolbar sx={{ gap: 2 }}>
                    <Tooltip title="Torna indietro" arrow>
                        <IconButton
                            edge="start"
                            onClick={() => setIsShowManageProxyLab(false)}
                            disabled={logic.isSubmitting}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <SecurityIcon sx={{ color: BRAND, fontSize: 28 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" noWrap sx={{ fontWeight: 'bold', color: BRAND, lineHeight: 1.2 }}>
                            Gestione Proxy
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.2 }}>
                            <Chip
                                size="small"
                                label={labProps.idLab}
                                sx={{
                                    height: 20, fontSize: '0.65rem', fontWeight: 700,
                                    backgroundColor: BRAND_LIGHT, color: BRAND,
                                    border: `1px solid ${BRAND_BORDER}`
                                }}
                            />
                            <Typography variant="body2" noWrap color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {labProps.labName}
                            </Typography>
                            {labProps.labDescription && (
                                <>
                                    <DotIcon sx={{ fontSize: 6, color: '#ccc' }} />
                                    <Typography variant="body2" noWrap sx={{ color: '#999', fontSize: '0.75rem' }}>
                                        {labProps.labDescription}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={() => setShowGlobalLists(true)}
                        startIcon={<PublicIcon />}
                        sx={{
                            textTransform: 'none', fontWeight: 600, mr: 1,
                            color: BRAND, borderColor: BRAND_BORDER,
                            '&:hover': { borderColor: BRAND, backgroundColor: BRAND_LIGHT }
                        }}
                    >
                        Liste Globali
                    </Button>
                    <Button
                        variant="contained"
                        disabled={logic.isSubmitting || logic.isLoading}
                        onClick={logic.handleSaveRules}
                        startIcon={logic.isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        sx={{
                            textTransform: 'none', fontWeight: 600, px: 3,
                            bgcolor: BRAND,
                            '&:hover': { bgcolor: BRAND_DARK },
                        }}
                    >
                        {logic.isSubmitting ? 'Salvataggio...' : 'Salva Regole'}
                    </Button>
                </Toolbar>
            </AppBar>

            {/* === BODY === */}
            <Box sx={{ backgroundColor: '#f4f4f4', minHeight: 'calc(100vh - 70px)', p: 3 }}>
                <Box sx={{ maxWidth: 1400, mx: 'auto' }}>

                    {/* === LOADING === */}
                    {logic.isLoading && (
                        <Fade in>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, flexDirection: 'column', gap: 2 }}>
                                <CircularProgress size={36} sx={{ color: BRAND }} />
                                <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Caricamento dati in corso...
                                </Typography>
                            </Box>
                        </Fade>
                    )}

                    {/* === FORM + TABELLE === */}
                    {!logic.isLoading && (
                        <Fade in timeout={500}>
                            <Box>
                                {/* Form aggiunta URL/Gruppi */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                                    {/* WHITELIST FORM */}
                                    <Paper elevation={1} sx={{
                                        p: 3, borderRadius: 2,
                                        border: '1px solid #e0e0e0',
                                        borderTop: '3px solid #2e7d32',
                                    }}>
                                        <SectionHeader
                                            icon={<ShieldIcon />}
                                            title="Whitelist"
                                            subtitle="Siti consentiti per questo laboratorio"
                                            accent="#2e7d32"
                                        />
                                        <Box sx={{ mb: 2 }}>
                                            <TextField
                                                label="Aggiungi URL"
                                                value={newUrl}
                                                onChange={(e) => setNewUrl(e.target.value)}
                                                fullWidth
                                                size="small"
                                                placeholder="es. example.com"
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddUrlClick(); }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <LanguageIcon sx={{ fontSize: 18, color: '#757575' }} />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: newUrl.trim() && (
                                                        <InputAdornment position="end">
                                                            <Tooltip title="Aggiungi URL" arrow>
                                                                <IconButton size="small" onClick={handleAddUrlClick}
                                                                    sx={{ bgcolor: '#2e7d32', color: '#fff', '&:hover': { bgcolor: '#1b5e20' } }}>
                                                                    <AddIcon sx={{ fontSize: 18 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<GroupIcon />}
                                            onClick={() => setShowGroupDialog(true)}
                                            disabled={logic.loadingGroups}
                                            sx={{
                                                textTransform: 'none', fontWeight: 600, py: 1,
                                                color: '#2e7d32', borderColor: 'rgba(46,125,50,0.3)',
                                                '&:hover': { borderColor: '#2e7d32', backgroundColor: 'rgba(46,125,50,0.04)' }
                                            }}
                                        >
                                            {logic.loadingGroups ? 'Caricamento...' : 'Seleziona Gruppo'}
                                        </Button>
                                    </Paper>

                                    {/* BLACKLIST FORM */}
                                    <Paper elevation={1} sx={{
                                        p: 3, borderRadius: 2,
                                        border: '1px solid #e0e0e0',
                                        borderTop: '3px solid #c62828',
                                    }}>
                                        <SectionHeader
                                            icon={<BlockIcon />}
                                            title="Blacklist"
                                            subtitle="Siti bloccati per questo laboratorio"
                                            accent="#c62828"
                                        />
                                        <Box sx={{ mb: 2 }}>
                                            <TextField
                                                label="Aggiungi URL"
                                                value={newBlacklistUrl}
                                                onChange={(e) => setNewBlacklistUrl(e.target.value)}
                                                fullWidth
                                                size="small"
                                                placeholder="es. example.com"
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddBlacklistUrlClick(); }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <LanguageIcon sx={{ fontSize: 18, color: '#757575' }} />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: newBlacklistUrl.trim() && (
                                                        <InputAdornment position="end">
                                                            <Tooltip title="Aggiungi URL" arrow>
                                                                <IconButton size="small" onClick={handleAddBlacklistUrlClick}
                                                                    sx={{ bgcolor: '#c62828', color: '#fff', '&:hover': { bgcolor: '#b71c1c' } }}>
                                                                    <AddIcon sx={{ fontSize: 18 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<GroupIcon />}
                                            onClick={() => setShowBlacklistGroupDialog(true)}
                                            disabled={logic.loadingGroups}
                                            sx={{
                                                textTransform: 'none', fontWeight: 600, py: 1,
                                                color: '#c62828', borderColor: 'rgba(198,40,40,0.3)',
                                                '&:hover': { borderColor: '#c62828', backgroundColor: 'rgba(198,40,40,0.04)' }
                                            }}
                                        >
                                            {logic.loadingGroups ? 'Caricamento...' : 'Seleziona Gruppo'}
                                        </Button>
                                    </Paper>
                                </Box>

                                {/* Tabelle Whitelist e Blacklist */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                                    <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2e7d32' }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                                Whitelist
                                            </Typography>
                                            <Chip label={logic.whitelistUrls.length} size="small"
                                                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(46,125,50,0.08)', color: '#2e7d32' }} />
                                        </Box>
                                        <WhitelistTable
                                            data={logic.whitelistUrls}
                                            editingUrl={logic.editingUrl}
                                            editUrlValue={logic.editUrlValue}
                                            onEditValueChange={logic.setEditUrlValue}
                                            onSaveEdit={logic.handleEditSave}
                                            onCancelEdit={logic.handleEditCancel}
                                            onStartEdit={logic.handleEditStart}
                                            onDelete={logic.handleDeleteItem}
                                            onViewDetails={handleViewDetails}
                                        />
                                    </Paper>

                                    <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#c62828' }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#c62828' }}>
                                                Blacklist
                                            </Typography>
                                            <Chip label={logic.blacklistUrls.length} size="small"
                                                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(198,40,40,0.08)', color: '#c62828' }} />
                                        </Box>
                                        <BlacklistTable
                                            data={logic.blacklistUrls}
                                            editingUrl={logic.editingBlacklistUrl}
                                            editUrlValue={logic.editBlacklistUrlValue}
                                            onEditValueChange={logic.setEditBlacklistUrlValue}
                                            onSaveEdit={logic.handleBlacklistEditSave}
                                            onCancelEdit={logic.handleBlacklistEditCancel}
                                            onStartEdit={logic.handleBlacklistEditStart}
                                            onDelete={logic.handleBlacklistDeleteItem}
                                            onViewDetails={handleViewDetails}
                                        />
                                    </Paper>
                                </Box>

                                {/* VM Accordion */}
                                <Paper elevation={1} sx={{ borderRadius: 2, border: '1px solid #e0e0e0', mb: 2, overflow: 'hidden' }}>
                                    <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent' }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                                            sx={{ px: 3, '&:hover': { bgcolor: '#fafafa' } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                <Box sx={{
                                                    width: 34, height: 34, borderRadius: 1.5,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    bgcolor: 'rgba(21,101,192,0.08)',
                                                }}>
                                                    <ComputerIcon sx={{ color: '#1565c0', fontSize: 18 }} />
                                                </Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                                                    VM Accese
                                                </Typography>
                                                <Chip
                                                    label={logic.vmIpAddresses.length}
                                                    size="small"
                                                    color={logic.vmIpAddresses.length > 0 ? 'success' : 'default'}
                                                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                                                />
                                                <Box sx={{ flex: 1 }} />
                                                <Tooltip title="Aggiorna elenco VM" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); logic.loadVmList && logic.loadVmList(); }}
                                                        disabled={logic.loadingVm}
                                                        sx={{ color: '#1565c0' }}
                                                    >
                                                        <RefreshIcon fontSize="small" sx={{
                                                            animation: logic.loadingVm ? 'spin 1s linear infinite' : 'none',
                                                            '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } }
                                                        }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0, borderTop: '1px solid #e0e0e0' }}>
                                            {logic.loadingVm ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                    <CircularProgress size={24} sx={{ color: BRAND }} />
                                                    <Typography variant="body2" color="text.secondary">Caricamento VM...</Typography>
                                                </Box>
                                            ) : logic.vmError ? (
                                                <Alert severity="error" sx={{ m: 2 }}>{logic.vmError}</Alert>
                                            ) : logic.vmList.length > 0 ? (
                                                <TableContainer sx={{ maxHeight: 300 }}>
                                                    <Table size="small" stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: '#fafafa' }}>Hostname</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: '#fafafa' }}>Indirizzo IP</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {logic.vmList.map((vm, index) => (
                                                                <TableRow key={index} hover>
                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <DnsIcon sx={{ fontSize: 14, color: '#1565c0' }} />
                                                                            {vm.dns_name || vm.name || '-'}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: '#1565c0', fontWeight: 600 }}>
                                                                        {vm.agent_ipv4_address}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            ) : (
                                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                                    <ComputerIcon sx={{ fontSize: 36, color: '#ccc', mb: 1 }} />
                                                    <Typography color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                                                        Nessuna VM accesa trovata
                                                    </Typography>
                                                </Box>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                </Paper>

                                {/* Stato Attuale Proxy Accordion */}
                                <Paper elevation={1} sx={{ borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                                    <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent' }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                                            sx={{ px: 3, '&:hover': { bgcolor: '#fafafa' } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                <Box sx={{
                                                    width: 34, height: 34, borderRadius: 1.5,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    bgcolor: BRAND_LIGHT,
                                                }}>
                                                    <PublicIcon sx={{ color: BRAND, fontSize: 18 }} />
                                                </Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                                                    Stato Attuale Proxy
                                                </Typography>
                                                {logic.proxyServiceInfo && (
                                                    <Chip
                                                        icon={<DotIcon sx={{ fontSize: '10px !important', color: '#2e7d32 !important' }} />}
                                                        label="Online"
                                                        size="small"
                                                        sx={{
                                                            height: 22, fontSize: '0.65rem', fontWeight: 600,
                                                            bgcolor: 'rgba(46,125,50,0.08)', color: '#2e7d32',
                                                            border: '1px solid rgba(46,125,50,0.2)',
                                                        }}
                                                    />
                                                )}
                                                <Box sx={{ flex: 1 }} />
                                                <Tooltip title="Aggiorna stato proxy" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); logic.loadProxyServiceInfo && logic.loadProxyServiceInfo(); }}
                                                        disabled={logic.loadingProxyInfo}
                                                        sx={{ color: BRAND }}
                                                    >
                                                        <RefreshIcon fontSize="small" sx={{
                                                            animation: logic.loadingProxyInfo ? 'spin 1s linear infinite' : 'none',
                                                            '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } }
                                                        }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 3, pb: 3, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                            {logic.loadingProxyInfo ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                    <CircularProgress size={24} sx={{ color: BRAND }} />
                                                    <Typography variant="body2" color="text.secondary">Caricamento stato proxy...</Typography>
                                                </Box>
                                            ) : logic.proxyServiceInfo ? (
                                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                                    {/* Allowed Sites */}
                                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, borderColor: 'rgba(46,125,50,0.2)', bgcolor: 'rgba(46,125,50,0.02)' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                            <CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                                                Allowed Sites
                                                            </Typography>
                                                            <Chip label={(logic.proxyServiceInfo.allowed_sites || []).length} size="small"
                                                                sx={{ height: 18, fontSize: '0.6rem', ml: 'auto', bgcolor: 'rgba(46,125,50,0.08)', color: '#2e7d32' }} />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {(logic.proxyServiceInfo.allowed_sites || []).length > 0
                                                                ? (logic.proxyServiceInfo.allowed_sites || []).map((site, i) => (
                                                                    <Chip key={i} label={site} size="small" variant="outlined"
                                                                        sx={{ fontSize: '0.7rem', borderColor: 'rgba(46,125,50,0.3)', color: '#2e7d32' }} />
                                                                ))
                                                                : <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Nessuno</Typography>
                                                            }
                                                        </Box>
                                                    </Paper>
                                                    {/* Blocked Sites */}
                                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, borderColor: 'rgba(198,40,40,0.2)', bgcolor: 'rgba(198,40,40,0.02)' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                            <BlockIcon sx={{ fontSize: 16, color: '#c62828' }} />
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#c62828' }}>
                                                                Blocked Sites
                                                            </Typography>
                                                            <Chip label={(logic.proxyServiceInfo.blocked_sites || []).length} size="small"
                                                                sx={{ height: 18, fontSize: '0.6rem', ml: 'auto', bgcolor: 'rgba(198,40,40,0.08)', color: '#c62828' }} />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {(logic.proxyServiceInfo.blocked_sites || []).length > 0
                                                                ? (logic.proxyServiceInfo.blocked_sites || []).map((site, i) => (
                                                                    <Chip key={i} label={site} size="small" variant="outlined"
                                                                        sx={{ fontSize: '0.7rem', borderColor: 'rgba(198,40,40,0.3)', color: '#c62828' }} />
                                                                ))
                                                                : <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Nessuno</Typography>
                                                            }
                                                        </Box>
                                                    </Paper>
                                                    {/* Addresses */}
                                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, borderColor: 'rgba(21,101,192,0.2)', bgcolor: 'rgba(21,101,192,0.02)' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                            <DnsIcon sx={{ fontSize: 16, color: '#1565c0' }} />
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1565c0' }}>
                                                                Indirizzi VM
                                                            </Typography>
                                                            <Chip label={(logic.proxyServiceInfo.addresses || []).length} size="small"
                                                                sx={{ height: 18, fontSize: '0.6rem', ml: 'auto', bgcolor: 'rgba(21,101,192,0.08)', color: '#1565c0' }} />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {(logic.proxyServiceInfo.addresses || []).length > 0
                                                                ? (logic.proxyServiceInfo.addresses || []).map((addr, i) => (
                                                                    <Chip key={i} label={addr} size="small" variant="outlined"
                                                                        sx={{ fontSize: '0.7rem', fontFamily: 'monospace', borderColor: 'rgba(21,101,192,0.3)', color: '#1565c0' }} />
                                                                ))
                                                                : <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Nessuno</Typography>
                                                            }
                                                        </Box>
                                                    </Paper>
                                                    {/* Docenti */}
                                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, borderColor: 'rgba(167,30,59,0.2)', bgcolor: BRAND_LIGHT }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                            <SchoolIcon sx={{ fontSize: 16, color: BRAND }} />
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: BRAND }}>
                                                                Docenti
                                                            </Typography>
                                                            <Chip label={(logic.proxyServiceInfo.docenti || []).length} size="small"
                                                                sx={{ height: 18, fontSize: '0.6rem', ml: 'auto', bgcolor: BRAND_LIGHT, color: BRAND }} />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {(logic.proxyServiceInfo.docenti || []).length > 0
                                                                ? (logic.proxyServiceInfo.docenti || []).map((doc, i) => (
                                                                    <Chip key={i} label={doc} size="small" variant="outlined"
                                                                        sx={{ fontSize: '0.7rem', borderColor: BRAND_BORDER, color: BRAND }} />
                                                                ))
                                                                : <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Nessuno</Typography>
                                                            }
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            ) : (
                                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                                    <PublicIcon sx={{ fontSize: 36, color: '#ccc', mb: 1 }} />
                                                    <Typography color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                                                        Nessun dato disponibile dal servizio proxy
                                                    </Typography>
                                                </Box>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                </Paper>
                            </Box>
                        </Fade>
                    )}
                </Box>
            </Box>

            {/* === DIALOG LISTE GLOBALI === */}
            <Dialog
                open={showGlobalLists}
                onClose={() => setShowGlobalLists(false)}
                fullScreen
                TransitionComponent={SlideTransition}
            >
                <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#1a237e' }}>
                    <Toolbar sx={{ gap: 2 }}>
                        <IconButton edge="start" color="inherit" onClick={() => setShowGlobalLists(false)}>
                            <CloseIcon />
                        </IconButton>
                        <PublicIcon />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Liste Globali
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.8rem' }}>
                                Queste liste si applicano a tutti i laboratori
                            </Typography>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto', width: '100%', bgcolor: '#f4f4f4', minHeight: '100%' }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Le modifiche alle liste globali hanno effetto su <strong>tutti i laboratori</strong>, non solo su quello corrente.
                    </Alert>
                    {logic.loadingGlobalLists ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, flexDirection: 'column', gap: 2 }}>
                            <CircularProgress size={32} sx={{ color: BRAND }} />
                            <Typography color="text.secondary">Caricamento liste globali...</Typography>
                        </Box>
                    ) : (
                        <GlobalListsTable
                            whitelistItems={logic.globalWhitelist}
                            onDeleteWhitelist={logic.handleDeleteGlobalWhitelist}
                            newWhitelistUrl={logic.newGlobalWhitelistUrl}
                            onNewWhitelistUrlChange={logic.setNewGlobalWhitelistUrl}
                            onAddWhitelist={logic.handleAddNewGlobalWhitelist}
                            loading={logic.isSubmitting}
                            onOpenGroupDialog={() => setShowGlobalGroupDialog(true)}
                            loadingGroups={logic.loadingGroups}
                        />
                    )}
                </Box>
            </Dialog>

            {/* === SNACKBAR === */}
            <Snackbar
                open={Boolean(logic.status)}
                autoHideDuration={4000}
                onClose={() => logic.setStatus(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {logic.status ? (
                    <Alert
                        severity={logic.status.type}
                        onClose={() => logic.setStatus(null)}
                        variant="filled"
                        sx={{ width: '100%', minWidth: 350, fontWeight: 600 }}
                    >
                        {logic.status.message}
                    </Alert>
                ) : undefined}
            </Snackbar>

            {/* === DIALOGS === */}
            <GroupSelectionDialog
                open={showGroupDialog}
                onClose={() => setShowGroupDialog(false)}
                availableGroups={logic.availableGroups}
                selectedGroups={logic.selectedGroups}
                loading={logic.loadingGroups}
                onViewDetails={handleViewDetails}
                onEditGroup={handleEditGroup}
                onDeleteGroup={logic.handleDeleteGroup}
                onAddGroup={(group) => {
                    logic.handleAddSingleGroup(group);
                    setShowGroupDialog(false);
                }}
                onCreateNew={() => {
                    setShowGroupDialog(false);
                    setShowCreateGroupDialog(true);
                }}
            />

            <GroupSelectionDialog
                open={showBlacklistGroupDialog}
                onClose={() => setShowBlacklistGroupDialog(false)}
                availableGroups={logic.availableGroups}
                selectedGroups={logic.selectedBlacklistGroups}
                loading={logic.loadingGroups}
                onViewDetails={handleViewDetails}
                onEditGroup={handleEditGroup}
                onDeleteGroup={logic.handleDeleteGroup}
                onAddGroup={(group) => {
                    logic.handleAddSingleBlacklistGroup(group);
                    setShowBlacklistGroupDialog(false);
                }}
                onCreateNew={() => {
                    setShowBlacklistGroupDialog(false);
                    setShowCreateGroupDialog(true);
                }}
            />

            <GroupSelectionDialog
                open={showGlobalGroupDialog}
                onClose={() => setShowGlobalGroupDialog(false)}
                availableGroups={logic.availableGroups}
                selectedGroups={[]}
                loading={logic.loadingGroups}
                onViewDetails={handleViewDetails}
                onEditGroup={handleEditGroup}
                onDeleteGroup={logic.handleDeleteGroup}
                onAddGroup={(group) => {
                    logic.handleAddGlobalGroup(group);
                    setShowGlobalGroupDialog(false);
                }}
                onCreateNew={() => {
                    setShowGlobalGroupDialog(false);
                    setShowCreateGroupDialog(true);
                }}
            />

            <CreateGroupDialog
                open={showCreateGroupDialog}
                onClose={() => setShowCreateGroupDialog(false)}
                onCreate={(payload) => {
                    return logic.handleCreateNewGroup(payload).then(() => setShowCreateGroupDialog(false));
                }}
            />

            <GroupDetailsDialog
                group={groupDetailsDialog}
                onClose={() => setGroupDetailsDialog(null)}
                onEdit={(group) => {
                    setGroupDetailsDialog(null);
                    setEditGroupDialog(group);
                }}
                onDelete={logic.handleDeleteGroup}
                isInWhitelist={groupDetailsDialog ? isGroupInWhitelist(groupDetailsDialog.idProxyGroup) : false}
            />

            <EditGroupDialog
                open={Boolean(editGroupDialog)}
                group={editGroupDialog}
                onClose={() => setEditGroupDialog(null)}
                onSave={logic.handleEditGroup}
            />
        </React.Fragment>
    );
}

LabConsolleManageProxyPage.propTypes = {
    labProps: PropTypes.object,
    apiAuthResponse: PropTypes.string,
    setIsShowManageProxyLab: PropTypes.func.isRequired
};
